import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Globe, Store, User, Sparkles, Trash2, History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { CustomerPicker } from "@/components/CustomerPicker";
import { AiConversation } from "@/components/AiConversation";
import { api, errMsg, fdatetime } from "@/lib/api";

const SCOPES = [["general", "Generale", Globe], ["store", "Punto vendita", Store], ["customer", "Cliente", User]];
const SCOPE_LABEL = { general: "Generale", store: "Punto vendita", customer: "Cliente" };

export default function AiAnalyst() {
  const [params, setParams] = useSearchParams();
  const [scope, setScope] = useState(params.get("scope") || "general");
  const [storeId, setStoreId] = useState(params.get("scope") === "store" ? params.get("id") || "" : "");
  const [customer, setCustomer] = useState(params.get("scope") === "customer" && params.get("id") ? { id: params.get("id"), name: "" } : null);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("claude-sonnet-5-5");
  const [stores, setStores] = useState([]);
  const [focus, setFocus] = useState("");
  const [kind, setKind] = useState(params.get("kind") === "promo" ? "promo" : "full");
  const [history, setHistory] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [streaming, setStreaming] = useState(false);

  const scopeId = scope === "store" ? storeId : scope === "customer" ? customer?.id || "" : "";
  const loadHistory = useCallback(() => {
    api.get("/ai/analyses", { params: { scope_type: scope, scope_id: scopeId } }).then((r) => setHistory(r.data)).catch(() => {});
  }, [scope, scopeId]);

  useEffect(() => {
    api.get("/ai/models").then((r) => setModels(r.data));
    api.get("/stores").then((r) => setStores(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    loadHistory();
    setParams({ scope, ...(scopeId ? { id: scopeId } : {}), ...(kind === "promo" ? { kind } : {}) }, { replace: true });
  }, [scope, scopeId, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const poll = useRef(null);
  useEffect(() => () => clearTimeout(poll.current), []);
  useEffect(() => {
    if (customer?.id && !customer.name) api.get(`/customers/${customer.id}`).then((r) => setCustomer({ ...r.data.customer })).catch(() => {});
  }, [customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const watch = (id) => {
    clearTimeout(poll.current);
    const tick = async () => {
      try {
        const { data } = await api.get(`/ai/analyses/${id}`);
        setAnalysis(data);
        if (data.status === "running") { poll.current = setTimeout(tick, 1200); return; }
        const last = data.messages.at(-1);
        if (last?.status === "error") toast.error(last.error || "Risposta interrotta");
      } catch (e) { toast.error(errMsg(e)); }
      setStreaming(false);
      loadHistory();
    };
    tick();
  };

  const start = async () => {
    if (scope !== "general" && !scopeId) return toast.error(scope === "store" ? "Scegli un punto vendita" : "Scegli un cliente");
    setStreaming(true);
    setAnalysis({ scope_type: scope, kind, scope_name: "Preparazione dati…", model, status: "running", messages: [{ role: "assistant", content: "", status: "running" }] });
    try {
      const { data } = await api.post("/ai/analyses", { scope_type: scope, scope_id: scopeId, model, focus, kind });
      loadHistory();
      watch(data.id);
    } catch (e) { toast.error(errMsg(e)); setStreaming(false); setAnalysis(null); }
  };
  const ask = async (text) => {
    setStreaming(true);
    setAnalysis((a) => ({ ...a, messages: [...a.messages, { role: "user", content: text }, { role: "assistant", content: "", status: "running" }] }));
    try { await api.post(`/ai/analyses/${analysis.id}/messages`, { text, model }); watch(analysis.id); } catch (e) { toast.error(errMsg(e)); watch(analysis.id); }
  };

  const open = async (h) => {
    try {
      const { data } = await api.get(`/ai/analyses/${h.id}`);
      setAnalysis(data);
      setModel(data.model);
      if (data.status === "running") { setStreaming(true); watch(h.id); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (h) => {
    if (!window.confirm("Eliminare questa analisi?")) return;
    try { await api.delete(`/ai/analyses/${h.id}`); toast.success("Analisi eliminata"); if (analysis?.id === h.id) setAnalysis(null); loadHistory(); } catch (e) { toast.error(errMsg(e)); }
  };
  const modelLabel = (id) => models.find((m) => m.id === id)?.label || id;

  return (
    <div>
      <PageHeader title="Agenti IA" subtitle="Analisi automatiche dei dati: rete, punti vendita e profilo dei singoli clienti" />
      <div className="grid gap-6 xl:grid-cols-[360px_1fr] [&>*]:min-w-0">
        <div className="space-y-6 xl:sticky xl:top-20 xl:self-start">
          <Panel title="Nuova analisi" testId="ai-setup-panel">
            <div className="space-y-4 p-5">
              <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1">
                {SCOPES.map(([k, l, Icon]) => (
                  <button key={k} data-testid={`ai-scope-${k}`} onClick={() => { setScope(k); setAnalysis(null); }}
                    className={`flex flex-col items-center gap-1 rounded-md py-2 text-xs font-medium transition-colors ${scope === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                    <Icon className="h-4 w-4" />{l}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-1 rounded-lg border border-slate-200 p-1">
                {[["full", "Analisi completa"], ["promo", "Impatto promozioni"]].map(([k, l]) => (
                  <button key={k} data-testid={`ai-kind-${k}`} onClick={() => setKind(k)}
                    className={`rounded-md py-1.5 text-xs font-medium transition-colors ${kind === k ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-50"}`}>{l}</button>
                ))}
              </div>
              {scope === "store" && (
                <Select value={storeId} onValueChange={setStoreId}>
                  <SelectTrigger data-testid="ai-store-select"><SelectValue placeholder="Scegli punto vendita" /></SelectTrigger>
                  <SelectContent className="max-h-80 bg-white">{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {scope === "customer" && <CustomerPicker testId="ai-customer" value={customer?.name ? customer : analysis?.scope_type === "customer" ? { name: analysis.scope_name } : customer} onPick={setCustomer} />}
              {scope === "customer" && <p className="text-xs text-slate-500">L'agente costruisce il profilo del cliente partendo dal suo paniere di spesa degli ultimi 6 mesi.</p>}
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-slate-600">Modello IA</span>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {models.map((m) => <SelectItem key={m.id} value={m.id}><span className="font-medium">{m.label}</span><span className="ml-2 text-xs text-slate-400">{m.hint}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input data-testid="ai-focus-input" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus opzionale (es. prodotti freschi, promo)" />
              <Button data-testid="ai-start-button" onClick={start} disabled={streaming} className="w-full bg-blue-600 hover:bg-blue-700">
                <Sparkles className="mr-2 h-4 w-4" />{streaming ? "Analisi in corso…" : "Avvia analisi"}
              </Button>
            </div>
          </Panel>
          <Panel title={<span className="flex items-center gap-2"><History className="h-4 w-4" />Storico · {SCOPE_LABEL[scope]}</span>} testId="ai-history-panel">
            {history.length === 0 ? <Empty text="Nessuna analisi salvata per questa selezione." /> : (
              <div className="max-h-[320px] divide-y divide-slate-100 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className={`group flex gap-2 px-4 py-3 transition-colors hover:bg-slate-50 ${analysis?.id === h.id ? "bg-blue-50/60" : ""}`}>
                    <button className="min-w-0 flex-1 text-left" data-testid={`ai-history-item-${h.id}`} onClick={() => open(h)}>
                      <div className="truncate text-sm font-medium text-slate-800">{h.scope_name}</div>
                      <div className="text-[11px] text-slate-400">{fdatetime(h.created_at)} · {modelLabel(h.model)}{h.questions ? ` · ${h.questions} ${h.questions === 1 ? "domanda" : "domande"}` : ""}{h.kind === "promo" ? " · promo" : ""}{h.source === "demo" ? " · demo" : ""}{h.status === "running" ? " · in corso" : h.status === "error" ? " · interrotta" : ""}</div>
                    </button>
                    <button data-testid={`ai-history-delete-${h.id}`} onClick={() => remove(h)} className="text-slate-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
        <Panel testId="ai-result-panel" className="min-h-[520px]" title={analysis ? <span data-testid="ai-result-title">{analysis.scope_name}</span> : "Risultato"}
          action={analysis && <span className="text-xs text-slate-400">{modelLabel(analysis.model)}</span>}>
          {analysis ? <AiConversation analysis={analysis} streaming={streaming} onAsk={ask} modelLabel={modelLabel(model)} /> : (
            <div className="flex flex-col items-center justify-center gap-3 px-8 py-24 text-center">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-50 text-blue-600"><Sparkles className="h-6 w-6" /></div>
              <p className="max-w-sm text-sm text-slate-500">Scegli l'ambito, il modello e avvia l'analisi. Potrai poi fare domande di approfondimento: tutto viene salvato nello storico.</p>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
